import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DEFAULT_FILTERS } from "@/hooks/useFilters";
import employeesCanonical from "@/api/employees-canonical";

/**
 * Feature 9.1a — Drill-down active-filter chips.
 *
 * Renders a compact row of chips describing the *narrowing* filters that are
 * currently active (employee_ids, department_ids, transaction_types,
 * day_of_week). The chips complement the collapsible filter panel: the panel
 * is where the user *sets* filters; these chips are what the panel looks like
 * when collapsed + something has been filtered by drill-down interaction
 * (bar click, heatmap cell click, etc.).
 *
 * Clicking the × on any chip clears just that facet (`onClear(key, value)`).
 * The preset/date-range chips are intentionally NOT shown here — those are
 * always visible in the date-range control in the header.
 */
export default function ActiveFiltersChips({ filters, onClear, testId = "active-filters-chips" }) {
  const { t } = useTranslation();
  const empIds = filters?.employee_ids || [];
  const deptIds = filters?.department_ids || [];
  const txTypes = filters?.transaction_types || [];
  const dowList = filters?.day_of_week || [];
  const hasAny =
    empIds.length > 0 ||
    deptIds.length > 0 ||
    txTypes.length > 0 ||
    dowList.length > 0;

  // Lazy-load canonicals so we can resolve employee_id → display name for the
  // chip. Fires on mount (not only when empIds is present) so the index is
  // ready as soon as the first drill-down chip appears — otherwise the chip
  // briefly shows the raw employee code until the fetch resolves.
  const [empIndex, setEmpIndex] = useState({});
  useEffect(() => {
    let alive = true;
    employeesCanonical
      .list()
      .then((r) => {
        if (!alive) return;
        const idx = {};
        // The `/employees-canonical` endpoint returns a top-level array,
        // not an `{items: [...]}` envelope. Handle both shapes just in case.
        const list = Array.isArray(r.data) ? r.data : r.data?.items || [];
        list.forEach((c) => {
          (c.aliases || []).forEach((a) => {
            const code = String(a.code || "").toUpperCase();
            if (code) idx[code] = c.canonical_name;
          });
        });
        setEmpIndex(idx);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!hasAny) return null;

  const removeFromArray = (key, value) => {
    const next = (filters?.[key] || []).filter((v) => v !== value);
    onClear(key, next);
  };
  const removeFromArrayInt = (key, value) => {
    const next = (filters?.[key] || []).filter((v) => Number(v) !== Number(value));
    onClear(key, next);
  };

  const dayLong = t("days.long", { returnObjects: true });

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2"
      data-testid={testId}
    >
      <span className="text-[11px] font-semibold uppercase tracking-wider text-primary">
        Drill-down:
      </span>
      {empIds.map((id) => (
        <Chip
          key={`emp-${id}`}
          label={empIndex[id.toUpperCase()] || id}
          hint={id}
          onRemove={() => removeFromArray("employee_ids", id)}
          testId={`drilldown-chip-emp-${id}`}
        />
      ))}
      {txTypes.map((tt) => (
        <Chip
          key={`tt-${tt}`}
          label={tt}
          onRemove={() => removeFromArray("transaction_types", tt)}
          testId={`drilldown-chip-tt-${tt}`}
        />
      ))}
      {dowList.map((d) => {
        const idx = Number(d);
        const label = dayLong[idx] || `Día ${idx}`;
        return (
          <Chip
            key={`dow-${idx}`}
            label={label}
            onRemove={() => removeFromArrayInt("day_of_week", idx)}
            testId={`drilldown-chip-dow-${idx}`}
          />
        );
      })}
      {deptIds.map((d) => (
        <Chip
          key={`d-${d}`}
          label={`Dept ${d.slice(-6)}`}
          onRemove={() => removeFromArray("department_ids", d)}
          testId={`drilldown-chip-dept-${d}`}
        />
      ))}
      <button
        type="button"
        onClick={() => {
          // Clear all narrowing arrays but preserve preset/date/source.
          onClear("__all__", DEFAULT_FILTERS);
        }}
        className="ml-auto text-[11px] text-primary underline-offset-2 hover:underline"
        data-testid="drilldown-chip-clear-all"
      >
        Limpiar drill-down
      </button>
    </div>
  );
}

function Chip({ label, hint, onRemove, testId }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-background px-2.5 py-1 text-xs font-medium text-foreground"
      data-testid={testId}
    >
      <span className="max-w-[180px] truncate">{label}</span>
      {hint && (
        <span className="font-mono text-[10px] text-muted-foreground">{hint}</span>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="rounded-full p-0.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
        aria-label={`Quitar filtro ${label}`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}
